/* eslint-disable @typescript-eslint/no-unused-vars */
import { useDispatch, useSelector } from 'react-redux';
import React, { Dispatch, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Tooltip } from 'antd';
import { getSubEnvTable } from './env-list';
import {
  CommonAction,
  ReduxCommonActionType,
} from '../../../store/modules/common';
import { SystemMenuId } from '../../../model/system/menu';

import {
  ICommonSupportPageProps,
  IPageGameManageItem,
  ISelectOption,
} from '../../../model';
import {
  getGameManagementList,
  supportDeleteEnv,
  supportDeleteGame,
  supportGetServiceStatusByEnv,
  updateAdminGameCcid,
  updateAdminGameCode,
} from '../../../api/admin/game';

import { CommonListMenu } from '../../../components/list-menu';
import styles from './index.module.scss';
import { PaginationTable } from '../../../components/pagination-table';
import { FilterGroup } from '../../../components/filter-group';
import { wrapRequestArea } from '../../../lib/http/lib';
import {
  IApiGameManageRes,
  IAPIServiceConfigItem,
  RbacAdminPermObjectKey,
} from '../../../model/api-modules';
import {
  getGameManageStudioList,
  getJoinEnvGameList,
  isStudioCertified,
} from './lib';
import { getLocalTime, strToJson } from '../../../lib';
import {
  CommonButton,
  CommonButtonGroup,
  DangerButton,
} from '../../../components/button/main';
import { getAdminServiceList, getCompanyList } from '../../../api';
import { getUserNameBySite } from '../lib/columns';
import { SpriteSvg, SpriteSvgType } from '../../../components/sprite-svg';
import { CommonInput } from '../../../components/input';
import { ModalWrap } from '../../../components/modal-wrap';
import getFormatMessage from '../../../locales/get-format-message';
import { messageError, messageSuccess } from '../../../components/message';
import moment, { Moment } from 'moment';
import { uniqBy } from 'lodash';
import { GameEnvRegion, GameEnvType } from '../../../model/api-modules/env';
import downloadBalanceIcon from '../../../assets/svg/download-balance.svg';
import { exportGameManageXlsx } from './export';
import { getEnvTypeOptions, IEnvTask } from '../../lib';
import { CustomPopover } from '../../../components/custom-popover';
import { IRootState } from '../../../store/reducers';
import { AdminPermCheck } from '../components/admin-permission-check';

interface IFilterValues {
  game?: string[];
  env?: number[];
  envType?: GameEnvType[];
  service?: number[];
  region?: string[];
  timeRange?: [Moment, Moment];
  member?: string;
}

interface IGameManageStudioItem {
  value: string;
  text: string;
  label: JSX.Element;
}

export const AdminGameManage = (props: ICommonSupportPageProps) => {
  const { site } = props;
  const menuDispatch = useDispatch<Dispatch<CommonAction>>();
  const [expandRows, setExpandRows] = useState<string[]>([]);
  const [aggInfos, setAggInfos] = useState<IApiGameManageRes>();
  const [envTasks, setEnvTasks] = useState<IEnvTask[]>([]);
  const [studioList, setStudioList] = useState<IGameManageStudioItem[]>([]);
  const [selectedStudios, setSelectedStudios] = useState<string[]>([]);
  const [gameList, setGameList] = useState<IPageGameManageItem[]>([]);
  const [selectedGames, setSelectedGames] = useState<string[]>([]);
  const [selectedEnvs, setSelectedEnvs] = useState<Record<string, number[]>>(
    {}
  );
  const [serviceConfigs, setServiceConfigs] = useState<IAPIServiceConfigItem[]>(
    []
  );
  const [editRow, setEditRow] = useState<IPageGameManageItem>();
  const [editType, setEditType] = useState<number>(); // 1:编辑游戏代号 2:编辑ccid

  const [filterValues, setFilterValues] = useState<IFilterValues>({});
  const [showCcidError, setShowCcidError] = useState<boolean>(false);
  const isHandlingRef = useRef<boolean>(false);
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);

  menuDispatch({
    type: ReduxCommonActionType.SELECT_MENU_ID,
    result: SystemMenuId.TitleManage,
  });

  const [form] = Form.useForm();

  useEffect(() => {
    firstInit();
  }, []);

  useEffect(() => {
    if (!aggInfos) {
      return;
    }

    setGameList(
      getJoinEnvGameList(
        selectedStudios,
        aggInfos.gameList,
        aggInfos.studioList,
        aggInfos.envList,
        envTasks,
        aggInfos.serviceOpenList,
        serviceConfigs
      )
    );
  }, [aggInfos, envTasks, serviceConfigs, selectedStudios.join()]);

  useEffect(() => {
    if (!aggInfos) {
      return;
    }
    const allGames = getJoinEnvGameList(
      studioList.map((item) => item.value),
      aggInfos.gameList,
      aggInfos.studioList,
      aggInfos.envList,
      envTasks,
      aggInfos.serviceOpenList,
      serviceConfigs
    );

    const filterGames = getFilterGameList(allGames);
    const filterStudioList = aggInfos?.studioList.filter(
      (studio) =>
        filterGames.some((game) => game.StudioID === studio.StudioID) ||
        !Object.values(filterValues).flat().join()
    );

    setStudioList(
      getGameManageStudioList(
        filterStudioList,
        aggInfos?.companyList,
        allGames,
        async (studioID) => {
          await init();
          setSelectedStudios(
            selectedStudios.filter((item) => item !== studioID)
          );
        },
        userInfo
      )
    );
  }, [aggInfos, filterValues, selectedStudios]);

  useEffect(() => {
    const filterGames = selectedGames.filter((gameID) => {
      return gameList.some((gameInfo) => gameInfo.GameID === gameID);
    });
    setSelectedGames(filterGames);
  }, [gameList]);

  const firstInit = async () => {
    const listRes = await init();
    setSelectedStudios(listRes?.studioList.map((item) => item.StudioID) || []);
  };

  const init = async () => {
    const [listRes, companyList, serviceConfigs] = await Promise.all([
      wrapRequestArea(getGameManagementList, site),
      wrapRequestArea(getCompanyList, site),
      wrapRequestArea(getAdminServiceList, site),
    ]);

    if (!listRes || !companyList) {
      return;
    }

    listRes.companyList = companyList;
    setAggInfos(listRes);
    setServiceConfigs(serviceConfigs || []);
    return listRes;
  };

  const isSearchMemberInGame = (search = '', game: IPageGameManageItem) => {
    if (!search) {
      return true;
    }
    const gameStudioId = game.StudioID;
    const members = aggInfos?.studioMemberList.filter(
      (member) => member.StudioID === gameStudioId
    );
    return members
      ?.map((item) => item.UserID)
      .join()
      .toLowerCase()
      .includes(search);
  };

  const getFilterGameList = (
    gameList: IPageGameManageItem[],
    filterInfo?: IFilterValues
  ) => {
    const filters = filterInfo || filterValues;
    return gameList
      .filter((game) => {
        return (
          isSearchMemberInGame(filterValues.member, game) &&
          (filters.game?.includes(game.GameID) || !filters.game?.length) &&
          ((moment(game.CreateTime).isSameOrBefore(
            filterValues.timeRange?.[1]
          ) &&
            moment(game.CreateTime).isSameOrAfter(
              filterValues.timeRange?.[0]
            )) ||
            !filterValues.timeRange ||
            filterValues.timeRange?.some((item) => !item)) &&
          (game.envs.some((env) => filterValues.env?.includes(env.id)) ||
            !filters.env?.length) &&
          (game.envs.some((env) => filterValues.envType?.includes(env.Type)) ||
            !filters.envType?.length) &&
          (game.envs.some((env) => filterValues.region?.includes(env.Region)) ||
            !filters.region?.length) &&
          (game.envs.some((env) =>
            env.services.some((service) =>
              filterValues.service?.includes(service.ServiceID)
            )
          ) ||
            !filters.service?.length)
        );
      })
      .map((game) => {
        return {
          ...game,
          envs: game.envs
            .filter((env) => {
              return (
                (filters.region?.includes(env.Region) ||
                  !filters.region?.length) &&
                (filters.env?.includes(env.id) || !filters.env?.length) &&
                (filters.envType?.includes(env.Type) ||
                  !filters.envType?.length) &&
                (env.services
                  .map((service) => service.ServiceID)
                  .some((serviceId) => filters.service?.includes(serviceId)) ||
                  !filters.service?.length)
              );
            })
            .sort((a, b) => {
              if (b.Type === GameEnvType.Live && a.Type === GameEnvType.Dev) {
                return 1;
              }
              return -1;
            }),
        };
      });
  };

  const getOptions = () => {
    const gameOptions = getFilterGameList(gameList, {
      ...filterValues,
      game: [],
    }).map((game) => {
      return { label: `${game.Name}(${game.GameID})`, value: game.GameID };
    });
    const filterGames = getFilterGameList(gameList, {
      game: filterValues.game,
    });
    const envOptions = filterGames
      .map((game) => game.envs)
      .flat()
      .map((env) => {
        return { label: `${env.Name}(${env.id})`, value: env.id };
      });
    const serviceOptions = uniqBy(
      serviceConfigs
        .filter((serviceConfig) =>
          aggInfos?.serviceOpenList.some(
            (item) => item.ServiceID === serviceConfig.id
          )
        )
        .map((service) => {
          return { label: service.Name, value: service.id };
        }),
      'label'
    ).sort((a, b) => (a.label > b.label ? 1 : -1));
    return {
      game: gameOptions,
      env: envOptions,
      service: serviceOptions,
      region: Object.keys(GameEnvRegion).map((item) => {
        return {
          label: item,
          value: Object(GameEnvRegion)[item],
        };
      }),
    };
  };

  const onClickEdit = (record: IPageGameManageItem, type: number) => {
    setEditRow(record);
    setEditType(type);
  };

  const filterGames = getFilterGameList(gameList);

  const sendAllPostRequests = async (
    gosUrl: { envURL: string; envID: number }[],
    gameID: string,
    ccid: string
  ) => {
    try {
      const promises = gosUrl.map((item) => {
        return updateAdminGameCcid(item.envURL, item.envID, gameID, ccid);
      });

      const results = await Promise.all(promises);
      return results;
    } catch (error) {
      console.error('❌ 发起 POST 请求时发生错误，请求中断:', error);
      throw new Error(
        `请求失败，原因: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  };

  const closeCcidError = () => {
    setShowCcidError(false);
    setEditRow(undefined);
    setEditType(undefined);
  };

  const renderEditCcidError = () => {
    if (showCcidError) {
      return (
        <ModalWrap
          width="520px"
          oKText={getFormatMessage('confirm')}
          onOk={() => {
            closeCcidError();
            return Promise.resolve(true);
          }}
          onClose={closeCcidError}
          title="操作失败"
          className="fail-dialog-gauth"
        >
          <div>请重试 或 联系开发处理</div>
        </ModalWrap>
      );
    }
    return null;
  };

  const deleteHandler = (record?: IPageGameManageItem) => {
    const getBatchDeleteGameTable = () => {
      if (!selectedGames.length) {
        return null;
      }
      const games = gameList.filter(
        (game) =>
          selectedGames.includes(game.GameID) && !isGameCannotDelete(game)
      );
      return (
        <PaginationTable
          list={games}
          pagination={false}
          configFields={[
            {
              name: '游戏名(游戏ID)',
              key: 'GameID',
              tableExtension: {
                renderRow(_key, _val, record) {
                  return `${record.Name}(${record.GameID})`;
                },
              },
            },
            {
              name: '工作室名称(工作室ID)',
              key: 'studio',
              tableExtension: {
                renderRow(_key, _val, record) {
                  return `${record?.studioInfo?.Name}(${record.StudioID})`;
                },
              },
            },
          ]}
          tableExtension={{
            scroll: { maxY: 300 },
          }}
        />
      );
    };
    const getBatchDeleteEnvTable = () => {
      if (selectedGames.length) {
        return null;
      }
      const envs = gameList
        .map((item) => item.envs)
        .flat()
        .filter((env) => Object.values(selectedEnvs).flat().includes(env.id));

      return (
        <PaginationTable
          list={envs}
          pagination={false}
          configFields={[
            {
              name: '环境名(环境ID)',
              key: 'Name',
              tableExtension: {
                renderRow(_key, _val, record) {
                  return `${record?.Name}(${record.id})`;
                },
              },
            },
            {
              name: '游戏名(游戏ID)',
              key: 'game',
              tableExtension: {
                renderRow(_key, _val, record) {
                  const matchedGame = gameList.find(
                    (game) => game.GameID === record.GameID
                  );
                  return `${matchedGame?.Name}(${record.GameID})`;
                },
              },
            },
            {
              name: '工作室名称(工作室ID)',
              key: 'game',
              tableExtension: {
                renderRow(_key, _val, record) {
                  const matchedGame = gameList.find(
                    (game) => game.GameID === record.GameID
                  );
                  return `${matchedGame?.studioInfo?.Name}(${matchedGame?.StudioID})`;
                },
              },
            },
          ]}
          tableExtension={{
            scroll: { maxY: 300 },
          }}
        />
      );
    };
    if (!record) {
      const deleteTypeText = selectedGames.length ? '游戏' : '环境';
      ModalWrap.show({
        title: `删除${deleteTypeText}`,
        children: (
          <div>
            <div style={{ color: '#F54E4E' }}>
              提示:删除操作无法撤销，请谨慎操作
            </div>
            <div style={{ marginBottom: 8 }}>
              是否确认删除以下{deleteTypeText}？
            </div>
            {getBatchDeleteGameTable()}
            {getBatchDeleteEnvTable()}
          </div>
        ),
        oKText: '删除',
        cancelText: '取消',
        danger: true,
        async onOk() {
          let res: boolean[] = [];
          if (selectedGames.length) {
            const deleteGames = gameList
              .filter(
                (game) =>
                  selectedGames.includes(game.GameID) &&
                  !isGameCannotDelete(game)
              )
              .map((game) => supportDeleteGame(game.GameID, game.StudioID));
            res = await Promise.all(deleteGames);
          } else {
            const deleteEnvs = Object.keys(selectedEnvs)
              .map((gameID) => {
                return selectedEnvs[gameID].map((envID) => {
                  const matchedGame = gameList.find(
                    (game) => game.GameID === gameID
                  );
                  if (matchedGame) {
                    return supportDeleteEnv(
                      gameID,
                      envID,
                      matchedGame?.StudioID
                    );
                  }
                  return false;
                });
              })
              .flat();
            res = await Promise.all(deleteEnvs);
          }
          if (res.every(Boolean)) {
            messageSuccess('操作成功');
          }
          setSelectedEnvs({});
          setSelectedGames([]);
          init();
          return true;
        },
      });
      return;
    }
    ModalWrap.show({
      title: '删除游戏',
      children: (
        <div>
          <div>{'是否确认删除以下游戏?'}</div>
          <div>
            游戏：{record.Name}({record.GameID})
          </div>
          <div>
            所属工作室：{`${record.studioInfo?.Name}(${record.StudioID})`}
          </div>
        </div>
      ),
      oKText: '删除',
      cancelText: '取消',
      danger: true,
      async onOk() {
        const res = await supportDeleteGame(record.GameID, record.StudioID);
        setSelectedEnvs({});
        setSelectedGames([]);
        init();
        return res;
      },
    });
  };

  const isGameCannotDelete = (record?: IPageGameManageItem) => {
    return (
      isStudioCertified(record?.StudioID, aggInfos?.companyList) ||
      record?.envs.some((env) => env.services.length > 0)
    );
  };

  const setIsHandling = () => {
    setTimeout(() => {
      isHandlingRef.current = false;
    }, 1000);
  };

  const handleCcidAction = (e: any, record: IPageGameManageItem) => {
    if (isHandlingRef.current) return;
    isHandlingRef.current = true;
    setIsHandling();
    if (editRow?.GameID === record.GameID && editType === 2) {
      const recordExtensionField = JSON.parse(record.ExtensionField || '{}');
      const recordCcid = recordExtensionField?.ccid || '';
      const { value } = e.target;
      if (
        (value === recordCcid && value !== '0') ||
        (recordCcid === '0' && value === '')
      ) {
        setEditRow(undefined);
        setEditType(undefined);
        return;
      }
      if (value === '' && recordCcid !== '0') {
        ModalWrap.warn({
          title: '删除游戏CCID',
          oKText: getFormatMessage('confirm'),
          cancelText: getFormatMessage('cancel'),
          children: (
            <div>
              <div>
                {`是否确认删除 ${record?.Name || ''}(${
                  record?.GameID || ''
                }) 的CCID`}
              </div>
              <div>
                <span>原游戏CCID: </span>
                <span>{recordCcid || ''}</span>
              </div>
            </div>
          ),
          async onOk() {
            const gosUrl: {
              envURL: string;
              envID: number;
            }[] = [];
            record.envs.forEach((item) => {
              const data = strToJson<{
                EnvURL: string;
                EnvApiAddr: string;
              }>(item.GosParams || '{}');
              const envURL = data?.EnvApiAddr || data?.EnvURL;
              if (envURL) {
                const it = gosUrl.find((i) => i.envURL === envURL);
                if (!it) {
                  gosUrl.push({
                    envURL,
                    envID: item.id,
                  });
                }
              }
            });
            if (gosUrl.length !== 0) {
              return sendAllPostRequests(gosUrl, record.GameID, '0')
                .then(async (data) => {
                  console.log('最终结果:', data);
                  const errorItem = data.filter(
                    (it) => it === false || it !== 0
                  );
                  if (errorItem.length !== 0) {
                    setShowCcidError(true);
                    return true;
                  }
                  const res = await wrapRequestArea(
                    () =>
                      updateAdminGameCode(record.GameID, {
                        extensionField: {
                          ccid: '0',
                        },
                      }),
                    site
                  );
                  if (res) {
                    setEditRow({
                      ...editRow,
                      ExtensionField: JSON.stringify({
                        ccid: '0',
                      }),
                    });
                    await init();
                    setEditRow(undefined);
                    setEditType(undefined);
                    return true;
                  }
                  messageError('操作失败');
                  return false;
                })
                .catch((err) => {
                  setShowCcidError(true);
                  console.error('最终捕获的错误:', err);
                  return true;
                });
            }
            const res = await wrapRequestArea(
              () =>
                updateAdminGameCode(record.GameID, {
                  extensionField: {
                    ccid: '0',
                  },
                }),
              site
            );
            if (res) {
              setEditRow({
                ...editRow,
                ExtensionField: JSON.stringify({
                  ccid: '0',
                }),
              });
              await init();
              setEditRow(undefined);
              setEditType(undefined);
              return true;
            }
            return false;
          },
          onClose() {
            setEditRow(undefined);
            setEditType(undefined);
          },
        });
        return;
      }
      if (!/^[1-9]\d{0,11}$/.test(value)) {
        return messageError(
          '仅支持大于 0 纯数字，不含空格且长度不超过 12 个字符'
        );
      }
      ModalWrap.warn({
        title: '修改游戏CCID',
        oKText: getFormatMessage('confirm'),
        cancelText: getFormatMessage('cancel'),
        children: (
          <div>
            <div>
              {`是否确认修改 ${record?.Name || ''}(${
                record?.GameID || ''
              }) 的CCID`}
            </div>
            <div>
              <span>原游戏CCID: </span>
              <span>{recordCcid && recordCcid !== '0' ? recordCcid : ''}</span>
            </div>
            <div>
              <span>修改后游戏CCID: </span>
              <span>{value || ''}</span>
            </div>
          </div>
        ),
        async onOk() {
          const gosUrl: {
            envURL: string;
            envID: number;
          }[] = [];
          record.envs.forEach((item) => {
            const data = strToJson<{
              EnvURL: string;
              EnvApiAddr: string;
            }>(item.GosParams || '{}');
            const envURL = data?.EnvApiAddr || data?.EnvURL;
            if (envURL) {
              const it = gosUrl.find((i) => i.envURL === envURL);
              if (!it) {
                gosUrl.push({
                  envURL,
                  envID: item.id,
                });
              }
            }
          });
          if (gosUrl.length !== 0) {
            return sendAllPostRequests(gosUrl, record.GameID, value)
              .then(async (data) => {
                // 所有请求成功时处理结果
                console.log('最终结果:', data);
                const errorItem = data.filter((it) => it === false || it !== 0);
                if (errorItem.length !== 0) {
                  setShowCcidError(true);
                  return true;
                }
                const res = await wrapRequestArea(
                  () =>
                    updateAdminGameCode(record.GameID, {
                      extensionField: {
                        ccid: value,
                      },
                    }),
                  site
                );
                if (res) {
                  setEditRow({
                    ...editRow,
                    ExtensionField: JSON.stringify({
                      ccid: value,
                    }),
                  });
                  await init();
                  setEditRow(undefined);
                  setEditType(undefined);
                  return true;
                }
                messageError('操作失败');
                return false;
              })
              .catch((err) => {
                setShowCcidError(true);
                console.error('最终捕获的错误:', err);
                return true;
              });
          }
          const res = await wrapRequestArea(
            () =>
              updateAdminGameCode(record.GameID, {
                extensionField: {
                  ccid: value,
                },
              }),
            site
          );
          if (res) {
            setEditRow({
              ...editRow,
              ExtensionField: JSON.stringify({
                ccid: value,
              }),
            });
            await init();
            setEditRow(undefined);
            setEditType(undefined);
            return true;
          }

          return true;
        },
        onClose() {
          setEditRow(undefined);
          setEditType(undefined);
        },
      });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.filter}>
        <FilterGroup
          onFilterChange={(values: IFilterValues, changeFiled) => {
            setFilterValues(values);

            if (changeFiled === 'game') {
              setFilterValues({
                ...values,
                env: [],
              });
            }
          }}
          values={filterValues}
          filterItems={{
            items: [
              {
                type: 'multi-select',
                name: 'game',
                label: '游戏',
                options: getOptions().game,
              },
              {
                type: 'multi-select',
                name: 'env',
                label: '环境',
                options: getOptions().env,
              },
              {
                type: 'multi-select',
                name: 'envType',
                label: '环境类型',
                options: getEnvTypeOptions(),
                props: {
                  showSearchInput: false,
                },
              },
              {
                type: 'multi-select',
                name: 'service',
                label: '服务',
                options: getOptions().service,
              },
              {
                type: 'multi-select',
                name: 'region',
                label: '地区',
                options: getOptions().region,
              },
              {
                type: 'date-range',
                name: 'timeRange',
              },
              {
                name: 'reset',
                type: 'clear-btn',
                onClick: () => {
                  setFilterValues({});
                },
              },

              {
                name: 'export',
                render() {
                  return (
                    <CommonButton
                      iconButton
                      onClick={() => {
                        const exportStudioIds = studioList
                          .filter(
                            (studio) =>
                              selectedStudios.includes(studio.value) ||
                              !selectedStudios.length
                          )
                          .map((item) => item.value);
                        const exportStudios = aggInfos?.studioList.filter(
                          (studio) => exportStudioIds.includes(studio.StudioID)
                        );
                        exportGameManageXlsx(
                          exportStudios,
                          filterGames,
                          userInfo,
                          site
                        );
                      }}
                    >
                      <img src={downloadBalanceIcon} width={16} height={16} />
                    </CommonButton>
                  );
                },
              },
              {
                name: 'delete',
                render() {
                  const disabled =
                    !selectedGames.length &&
                    !Object.values(selectedEnvs).flat().length;
                  return (
                    <AdminPermCheck
                      contentType="action"
                      permissionKey={RbacAdminPermObjectKey.GameManageEdit}
                      otherTips={disabled ? '仅支持批量删除游戏或环境' : ''}
                    >
                      <DangerButton
                        iconButton
                        disabled={disabled}
                        onClick={() => deleteHandler()}
                      >
                        <SpriteSvg
                          type={SpriteSvgType.Delete}
                          width={15}
                          height={15}
                          fill={'#4E5969'}
                        />
                      </DangerButton>
                    </AdminPermCheck>
                  );
                },
              },
              {
                name: 'member',
                type: 'search',
                placeholder: '成员名称/邮箱',
                place: 'right',
              },
            ],
          }}
        />
      </div>
      <div className={styles.body}>
        <CommonListMenu
          selectedValues={selectedStudios}
          header="工作室列表"
          list={studioList}
          showSearch
          searchPlaceholder="工作室名称/ID"
          showSelectAll
          onSingleSelectChanged={(val) => {
            if (selectedStudios.length === studioList.length) {
              setSelectedStudios([val]);
              return;
            }
          }}
          onSelectChanged={(ids) => {
            setSelectedStudios(ids);
          }}
          onSearch={() => {
            setSelectedStudios([]);
          }}
        />

        <div className={styles.table}>
          <PaginationTable
            list={filterGames}
            tableExtension={{
              rowKey: 'GameID',

              expandDetail: {
                expandKeys: expandRows,
                colSpan: 9,
                async onChange(gameId, open, keys) {
                  setExpandRows(keys);
                  if (!open) {
                    return;
                  }
                  const record = filterGames.find(
                    (game) => game.GameID === gameId
                  );
                  if (!record) {
                    return;
                  }
                  const envs = record?.envs || [];
                  const envTaskRes = await Promise.all(
                    envs.map((env) =>
                      supportGetServiceStatusByEnv(
                        record.GameID,
                        env.id,
                        env.Guid
                      )
                    )
                  );
                  setEnvTasks(
                    uniqBy(
                      [
                        ...(envTaskRes.filter(Boolean) as IEnvTask[]),
                        ...envTasks,
                      ],
                      'env_id'
                    )
                  );
                },

                render(record) {
                  const rowItem = record;
                  return getSubEnvTable(
                    aggInfos?.companyList,
                    rowItem,
                    selectedEnvs[record.GameID],
                    !!selectedGames.length,
                    selectedGames.includes(record.GameID),
                    () => {
                      init();
                    },
                    (gameID, keys) => {
                      setSelectedEnvs({ ...selectedEnvs, [gameID]: keys });
                    },
                    userInfo,
                    site
                  );
                },
              },
              rowSelection: {
                selectedRowKeys: selectedGames,
                onChange: (
                  selectedRowKeys,
                  selectedRows,
                  changeRecord,
                  checked,
                  isHeaderCheck
                ) => {
                  if (!isHeaderCheck) {
                    setSelectedGames(selectedRowKeys as string[]);
                    return;
                  }
                  if (selectedRowKeys.length) {
                    const canDeleteGames = filterGames.filter(
                      (item) => !isGameCannotDelete(item)
                    );
                    setSelectedGames(canDeleteGames.map((item) => item.GameID));
                    return;
                  }

                  setSelectedGames([]);
                },
                getCheckboxProps(record) {
                  const canDeleteGames = filterGames.filter(
                    (item) => !isGameCannotDelete(item)
                  );
                  if (!record) {
                    return {
                      checked: canDeleteGames.length <= selectedGames.length,
                      indeterminate:
                        selectedGames.length > 0 &&
                        canDeleteGames.length > selectedGames.length,
                      disabled:
                        canDeleteGames.length === 0 ||
                        !!Object.values(selectedEnvs).flat().length,
                    };
                  }
                  return {
                    className: isGameCannotDelete(record)
                      ? styles.checkCannotSelect
                      : '',
                    disabled:
                      isGameCannotDelete(record) ||
                      !!Object.values(selectedEnvs).flat().length,
                  };
                },
              },
            }}
            configFields={[
              {
                key: 'GameID',
                name: '游戏ID',
              },
              {
                key: 'Name',
                name: '游戏名称',
              },
              {
                key: 'ProjectCode',
                name: '游戏代号',
                tableExtension: {
                  width: 120,
                  renderRow(_key, _val, record) {
                    if (editRow?.GameID === record.GameID && editType === 1) {
                      return (
                        <CommonInput
                          style={{ width: 120, height: 24, paddingLeft: 2 }}
                          autoFocus
                          defaultValue={record.ProjectCode || ''}
                          onBlur={(ev) => {
                            const { value } = ev.target;
                            if (!value || value === record.ProjectCode) {
                              setEditRow(undefined);
                              setEditType(undefined);
                              return;
                            }
                            if (!/^[a-zA-Z0-9]{1,9}$/.test(value)) {
                              return messageError(
                                getFormatMessage('code_is_required')
                              );
                            }
                            ModalWrap.warn({
                              title: '修改游戏代号',
                              oKText: getFormatMessage('confirm'),
                              cancelText: getFormatMessage('cancel'),
                              children: (
                                <div>
                                  <div>
                                    {`是否确认修改 ${record?.Name || ''}(${
                                      record?.GameID || ''
                                    }) 的游戏代号`}
                                  </div>
                                  <div>
                                    <span>原游戏代号: </span>
                                    <span>{record?.ProjectCode || ''}</span>
                                  </div>
                                  <div>
                                    <span>修改后: </span>
                                    <span>{value || ''}</span>
                                  </div>
                                </div>
                              ),
                              async onOk() {
                                const res = await wrapRequestArea(
                                  () =>
                                    updateAdminGameCode(record.GameID, {
                                      projectCode: value,
                                    }),
                                  site
                                );
                                if (res) {
                                  setEditRow({
                                    ...editRow,
                                    ProjectCode: value,
                                  });
                                  await init();
                                  setEditRow(undefined);
                                  setEditType(undefined);
                                  return true;
                                }
                                return false;
                              },
                              onClose() {
                                setEditRow(undefined);
                                setEditType(undefined);
                              },
                            });
                          }}
                        />
                      );
                    }
                    return (
                      <span className={styles.projectCodeCell}>
                        <span>
                          {editRow?.GameID === record.GameID && editType === 1
                            ? editRow.ProjectCode
                            : record.ProjectCode}
                        </span>
                        <AdminPermCheck
                          contentType="action"
                          permissionKey={RbacAdminPermObjectKey.GameManageEdit}
                        >
                          <a onClick={() => onClickEdit(record, 1)}>
                            <SpriteSvg
                              type={SpriteSvgType.Edit}
                              width={12}
                              height={12}
                            />
                          </a>
                        </AdminPermCheck>
                      </span>
                    );
                  },
                },
              },
              {
                key: 'ExtensionField',
                name: 'CCID',
                tableExtension: {
                  width: 120,
                  renderRow(_key, _val, record) {
                    const editRowExtensionField = JSON.parse(
                      editRow?.ExtensionField || '{}'
                    );
                    const editRowCcid = editRowExtensionField?.ccid || '';
                    const recordExtensionField = JSON.parse(
                      record.ExtensionField || '{}'
                    );
                    const recordCcid = recordExtensionField?.ccid || '';
                    let showValue =
                      editRow?.GameID === record.GameID && editType === 2
                        ? editRowCcid
                        : recordCcid;
                    // 删除ccid存0  输入框value为空是删除操作
                    if (showValue === '0') {
                      showValue = '';
                    }
                    if (editRow?.GameID === record.GameID && editType === 2) {
                      return (
                        <CommonInput
                          style={{ width: 120, height: 24, paddingLeft: 2 }}
                          autoFocus
                          defaultValue={
                            recordCcid && recordCcid !== '0' ? recordCcid : ''
                          }
                          onPressEnter={(e) => handleCcidAction(e, record)}
                          onBlur={(e) => handleCcidAction(e, record)}
                        />
                      );
                    }
                    return (
                      <span className={styles.projectCodeCell}>
                        <span>
                          {/* {editRow?.GameID === record.GameID && editType === 2
                            ? editRowCcid
                            : recordCcid} */}
                          {showValue}
                        </span>
                        <AdminPermCheck
                          contentType="action"
                          permissionKey={RbacAdminPermObjectKey.GameManageEdit}
                        >
                          <a onClick={() => onClickEdit(record, 2)}>
                            <SpriteSvg
                              type={SpriteSvgType.Edit}
                              width={12}
                              height={12}
                            />
                          </a>
                        </AdminPermCheck>
                      </span>
                    );
                  },
                },
              },
              {
                key: 'GameKey',
                name: '游戏Key',
                tableExtension: {
                  renderRow(_key, _val, record) {
                    return (
                      <CustomPopover content={record.GameKey}>
                        {`${record.GameKey.substr(
                          0,
                          6
                        )}******${record.GameKey.substr(
                          record.GameKey.length - 6
                        )}`}
                      </CustomPopover>
                    );
                  },
                },
              },
              {
                key: 'StudioID',
                name: '工作室',
                tableExtension: {
                  renderRow(_key, _val, record) {
                    return `${record.studioInfo?.Name || ''}(${
                      record.studioInfo?.StudioID || ''
                    })`;
                  },
                },
              },
              {
                key: 'Creator',
                name: '创建人',
                tableExtension: {
                  renderRow(_key, _val, record) {
                    return getUserNameBySite(record.Creator, site);
                  },
                },
              },
              {
                key: 'CreateTime',
                name: '创建时间',
                tableExtension: {
                  sortable: true,
                  sorter: (a, b) => {
                    return a.CreateTime > b.CreateTime ? 1 : -1;
                  },
                  renderRow(_key, _val, record) {
                    return getLocalTime(
                      record.CreateTime,
                      userInfo?.UserSettings?.Timezone
                    );
                  },
                },
              },
              {
                key: 'operate',
                name: '操作',
                tableExtension: {
                  renderRow(_key, _val, record) {
                    const getTip = () => {
                      if (
                        isStudioCertified(
                          record?.StudioID,
                          aggInfos?.companyList
                        )
                      ) {
                        return '已企业认证不支持删除';
                      }
                      if (record?.envs.some((env) => env.services.length > 0)) {
                        return '已开通服务不支持删除';
                      }
                    };
                    return (
                      <CommonButtonGroup>
                        <AdminPermCheck
                          contentType="action"
                          permissionKey={RbacAdminPermObjectKey.GameManageEdit}
                          placement="bottom"
                          otherTips={getTip()}
                        >
                          <CommonButton
                            type="link"
                            danger
                            disabled={isGameCannotDelete(record)}
                            onClick={() => deleteHandler(record)}
                          >
                            删除
                          </CommonButton>
                        </AdminPermCheck>
                      </CommonButtonGroup>
                    );
                  },
                },
              },
            ]}
          />
        </div>
      </div>
      {renderEditCcidError()}
    </div>
  );
};
